import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Cpu, ShieldCheck, Rocket, Package, Search, Filter as FilterIcon,
  Download, CheckCircle2, Cloud, Zap, BadgeCheck, Star, Layers, BookOpen,
  Video, FileText, GraduationCap, Lightbulb, ArrowRight, Play, Plus,
} from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";
import { displayReqNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SubmitSolutionDialog from "@/components/SubmitSolutionDialog";

function Kpi({ label, value, icon: Icon, accent, sub }) {
  return (
    <Card
      data-testid={`marketplace-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`p-4 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : ""}`}
    >
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={12} />
      </div>
      <div className={`font-display font-black text-2xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-[10px] mt-0.5 ${accent ? "text-white/70" : "text-slate-500"}`}>{sub}</div>}
    </Card>
  );
}

function ExecBar({ execution, compact = false }) {
  const { cpu, npu, gpu } = execution;
  return (
    <div className="w-full">
      <div className={`flex h-1.5 rounded-full overflow-hidden bg-slate-100 ${compact ? "" : "mt-1"}`}>
        <div className="bg-slate-400" style={{ width: `${cpu}%` }} title={`CPU ${cpu}%`} />
        <div className="bg-[#0033A0]" style={{ width: `${npu}%` }} title={`NPU ${npu}%`} />
        <div className="bg-[#DA291C]" style={{ width: `${gpu}%` }} title={`GPU ${gpu}%`} />
      </div>
      {!compact && (
        <div className="flex justify-between text-[9px] mt-1 text-slate-500">
          <span>CPU {cpu}%</span>
          <span className="text-[#0033A0] font-semibold">NPU {npu}%</span>
          <span>GPU {gpu}%</span>
        </div>
      )}
    </div>
  );
}

function statusColor(status) {
  if (status === "Approved") return "bg-[#0F9B7F]/10 text-[#0F9B7F] border-[#0F9B7F]/30";
  if (status === "Pilot") return "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

function AppCard({ app, onOpen }) {
  return (
    <Card
      data-testid={`app-card-${app.id}`}
      className="p-4 border-slate-200 shadow-sm hover:shadow-md hover:border-[#0033A0]/40 transition-all cursor-pointer flex flex-col"
      onClick={() => onOpen(app)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#0033A0] font-semibold">{app.category_label}</div>
          <div className="font-display font-bold text-slate-900 text-sm leading-tight mt-0.5 truncate">{app.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{app.vendor}</div>
        </div>
        <Badge variant="outline" className={`text-[9px] shrink-0 ${statusColor(app.status)}`}>{app.status}</Badge>
      </div>
      <p className="text-[11px] text-slate-600 mt-2 line-clamp-2 leading-relaxed">{app.description}</p>
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-0.5">
          <span className="uppercase tracking-widest font-semibold">Execution</span>
          <span className="font-mono">{app.tops_required}+ TOPS</span>
        </div>
        <ExecBar execution={app.execution} />
      </div>
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {app.on_device && <Badge variant="outline" className="bg-slate-50 border-slate-200 text-[9px] text-slate-600"><Cpu size={9} className="mr-1" />On-device</Badge>}
        {app.copilot_plus && <Badge className="bg-[#0033A0] hover:bg-[#0033A0] text-white text-[9px]"><Sparkles size={9} className="mr-1" />Copilot+</Badge>}
        {!app.on_device && <Badge variant="outline" className="bg-slate-50 border-slate-200 text-[9px] text-slate-600"><Cloud size={9} className="mr-1" />Cloud</Badge>}
      </div>
      <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><Star size={9} className="text-amber-500" /> {app.rating}</span>
        <span>{app.install_count.toLocaleString()} installs</span>
      </div>
    </Card>
  );
}

function ModelCard({ model, onDeploy }) {
  return (
    <Card
      data-testid={`model-card-${model.id}`}
      className="p-4 border-slate-200 shadow-sm hover:shadow-md hover:border-[#0033A0]/40 transition-all flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#0033A0] font-semibold">{model.modality}</div>
          <div className="font-display font-bold text-slate-900 text-sm leading-tight mt-0.5 truncate">{model.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{model.vendor} · {model.family}</div>
        </div>
        <Badge variant="outline" className={`text-[9px] shrink-0 ${statusColor(model.status)}`}>{model.status}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
        {model.params_b != null && (
          <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Params</div>
            <div className="font-mono font-bold text-slate-900">{model.params_b}B</div>
          </div>
        )}
        <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Quant</div>
          <div className="font-mono font-bold text-slate-900">{model.quantization}</div>
        </div>
        {model.ttft_ms != null && (
          <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">TTFT</div>
            <div className="font-mono font-bold text-slate-900">{model.ttft_ms} ms</div>
          </div>
        )}
        {model.tokens_per_sec != null && (
          <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Tokens/s</div>
            <div className="font-mono font-bold text-slate-900">{model.tokens_per_sec}</div>
          </div>
        )}
        {model.context_window ? (
          <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 col-span-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Context Window</div>
            <div className="font-mono font-bold text-slate-900">
              {typeof model.context_window === "number" && model.context_window >= 1000 ? `${(model.context_window / 1000).toFixed(0)}k` : model.context_window} tokens
            </div>
          </div>
        ) : null}
      </div>
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {model.npu_ready && <Badge className="bg-[#0033A0] hover:bg-[#0033A0] text-white text-[9px]"><Cpu size={9} className="mr-1" />NPU ready</Badge>}
        {model.openvino && <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-600">OpenVINO</Badge>}
        <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-600 truncate max-w-[140px]">{model.license}</Badge>
      </div>
      <div className="mt-2 text-[11px] text-slate-600 leading-relaxed">{model.notes}</div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
        <div className="text-[10px] text-slate-500 leading-tight">
          <span className="font-semibold text-slate-700">IT-provisioned</span> · runs on-device via OpenVINO
        </div>
        <Button
          data-testid={`request-deploy-${model.id}`}
          size="sm"
          onClick={() => onDeploy && onDeploy(model)}
          className="bg-[#0033A0] hover:bg-[#002573] text-white h-7 text-[11px] px-2.5"
        >
          <Download size={11} className="mr-1" />
          Request deployment
        </Button>
      </div>
    </Card>
  );
}

function AppDetailDialog({ app, open, onOpenChange, onInstall }) {
  if (!app) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="app-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 grid place-items-center bg-[#0033A0] text-white rounded-md">
              <Package size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{app.category_label}</div>
              <DialogTitle className="text-lg font-display font-bold text-slate-900">{app.name}</DialogTitle>
              <DialogDescription className="text-[11px] text-slate-500">{app.vendor} · {app.license}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <p className="text-sm text-slate-700 leading-relaxed">{app.description}</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Execution</div>
            <ExecBar execution={app.execution} />
            <div className="text-[11px] text-slate-500 mt-2">Minimum {app.tops_required}+ TOPS on device</div>
          </div>
          <div className="border border-slate-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">IT Assessment</div>
            <div className="text-sm font-semibold text-slate-900 mt-1">{app.assessment || "—"}</div>
            {app.latency_improvement_ms && (
              <div className="mt-2 text-[11px]">
                <span className="text-slate-500">Latency: </span>
                <span className="font-mono text-slate-500 line-through">{app.latency_improvement_ms.cpu_only}ms</span>
                <span className="mx-1 text-slate-400">→</span>
                <span className="font-mono text-[#0F9B7F] font-bold">{app.latency_improvement_ms.cpu_npu}ms</span>
              </div>
            )}
          </div>
        </div>

        <div className="border border-slate-200 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Validated Personas</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {app.personas.map((p) => (
              <Badge key={p} variant="outline" className="text-[10px] border-slate-300 text-slate-700 capitalize">{p.replace("_", " ")}</Badge>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><Star size={11} className="text-amber-500" /> {app.rating}</span>
            <span>·</span>
            <span>{app.install_count.toLocaleString()} enterprise installs</span>
          </div>
          <Button
            data-testid={`request-install-${app.id}`}
            onClick={() => onInstall(app)}
            className="bg-[#0033A0] hover:bg-[#002573] text-white"
          >
            <Download size={14} className="mr-1.5" />
            Request Install
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AIMarketplace() {
  const { persona, userName, userEmail } = usePersona();
  const { brand } = useBranding();
  const [tab, setTab] = useState("apps");
  const [apps, setApps] = useState([]);
  const [models, setModels] = useState([]);
  const [resources, setResources] = useState([]);
  const [featuredResources, setFeaturedResources] = useState([]);
  const [resourceKpis, setResourceKpis] = useState({});
  const [resourceTypes, setResourceTypes] = useState([]);
  const [resourceType, setResourceType] = useState("all");
  const [appKpis, setAppKpis] = useState({});
  const [modelKpis, setModelKpis] = useState({});
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [modality, setModality] = useState("all");
  const [q, setQ] = useState("");
  const [personaFilter, setPersonaFilter] = useState(true);
  const [loading, setLoading] = useState(true);
  const [openApp, setOpenApp] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    endpoints
      .marketplaceApps({
        persona: personaFilter ? persona.id : undefined,
        category: category === "all" ? undefined : category,
        q: q || undefined,
      })
      .then((res) => {
        setApps(res.items || []);
        setAppKpis(res.kpis || {});
        setCategories(res.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [persona.id, personaFilter, category, q]);

  useEffect(() => {
    endpoints
      .marketplaceModels({
        persona: personaFilter ? persona.id : undefined,
        modality: modality === "all" ? undefined : modality,
      })
      .then((res) => {
        setModels(res.items || []);
        setModelKpis(res.kpis || {});
      })
      .catch(() => {});
  }, [persona.id, personaFilter, modality]);

  useEffect(() => {
    endpoints
      .marketplaceResources({
        persona: personaFilter ? persona.id : undefined,
        type: resourceType === "all" ? undefined : resourceType,
      })
      .then((res) => {
        setResources(res.items || []);
        setFeaturedResources(res.featured || []);
        setResourceKpis(res.kpis || {});
        setResourceTypes(res.types || []);
      })
      .catch(() => {});
  }, [persona.id, personaFilter, resourceType]);

  const filteredCats = useMemo(() => [{ id: "all", label: "All" }, ...categories], [categories]);

  const requestInstall = async (app) => {
    try {
      const res = await endpoints.requestAppInstall(app.id, {
        app_id: app.id,
        persona_id: persona.id,
        user_name: userName || "Employee",
        user_email: userEmail || null,
        justification: `Needed for ${persona.name} workflow.`,
      });
      toast.success(`Install ticket ${displayReqNumber(res.ticket)} created`);
      setOpenApp(null);
    } catch (e) {
      toast.error("Could not create install ticket");
    }
  };

  const requestDeploy = async (model) => {
    try {
      const res = await endpoints.requestModelDeployment(model.id, {
        model_id: model.id,
        persona_id: persona.id,
        user_name: userName || "Employee",
        user_email: userEmail || null,
        justification: `Deploy ${model.name} for ${persona.name} on-device workloads.`,
      });
      toast.success(`Deployment ticket ${displayReqNumber(res.ticket) || "created"}`);
    } catch (e) {
      toast.error("Could not create deployment ticket");
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-marketplace-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
            <Sparkles size={12} className="text-[#0033A0]" /> Marketplace
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">
            Persona-validated apps & models for your PC
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Browse {brand.orgName}-approved AI applications and models. Each item is validated for its execution mix (CPU vs NPU vs GPU), TOPS requirement and persona fit — with a one-click install request that opens a ServiceNow ticket.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="submit-solution-open-btn"
            variant="outline"
            onClick={() => setSubmitOpen(true)}
            className="border-[#0033A0]/30 text-[#0033A0] hover:bg-[#0033A0]/5"
          >
            <Plus size={14} className="mr-1.5" />
            Submit solution
          </Button>
          <Button
            data-testid="marketplace-toggle-persona"
            variant={personaFilter ? "default" : "outline"}
            onClick={() => setPersonaFilter(!personaFilter)}
            className={personaFilter ? "bg-[#0033A0] hover:bg-[#002573] text-white" : ""}
          >
            <FilterIcon size={14} className="mr-2" />
            {personaFilter ? `${persona.name} only` : "Show all"}
          </Button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi label="Total Apps" value={appKpis.total_apps || 0} icon={Package} accent sub="Enterprise catalogue" />
        <Kpi label="Approved" value={appKpis.approved || 0} icon={CheckCircle2} sub="Green-lit for prod" />
        <Kpi label="Pilot" value={appKpis.pilot || 0} icon={Rocket} sub="Under evaluation" />
        <Kpi label="On-device" value={appKpis.on_device || 0} icon={Cpu} sub="NPU / local runtime" />
        <Kpi label="Models" value={modelKpis.total_models || 0} icon={Layers} sub={`${modelKpis.families || 0} families`} />
        <Kpi label="NPU-ready models" value={modelKpis.on_device || 0} icon={Zap} sub={`${modelKpis.openvino || 0} OpenVINO`} />
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="marketplace-tabs">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="apps" data-testid="tab-apps" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
            Apps ({apps.length})
          </TabsTrigger>
          <TabsTrigger value="models" data-testid="tab-models" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
            Models ({models.length})
          </TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
            Resources ({resources.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "apps" && (
        <>
          <Card className="p-3 border-slate-200 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="marketplace-search"
                  placeholder="Search apps by name, vendor or description"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9 bg-slate-50 border-slate-200"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {filteredCats.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`marketplace-cat-${c.id}`}
                    onClick={() => setCategory(c.id)}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                      category === c.id
                        ? "bg-[#0033A0] text-white border-[#0033A0]"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {c.label || c.id}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="apps-grid">
            {loading && Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-md" />)}
            {!loading && apps.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500 text-sm border border-dashed border-slate-300 rounded-md">
                No apps match your filters.
              </div>
            )}
            {apps.map((a) => <AppCard key={a.id} app={a} onOpen={setOpenApp} />)}
          </div>
        </>
      )}

      {tab === "models" && (
        <>
          <Card className="p-4 border-[#0033A0]/20 bg-[#0033A0]/5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-md bg-[#0033A0] text-white grid place-items-center shrink-0">
                <Cpu size={14} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-[#0033A0] font-bold">
                  What are these models · How to get them
                </div>
                <div className="text-[13px] text-slate-700 mt-1 leading-relaxed">
                  Curated small language & vision models, quantized to run <span className="font-semibold text-slate-900">on-device on your AI PC</span> via the Intel NPU (OpenVINO runtime). No cloud round-trip · no data leaving the endpoint.
                </div>
                <div className="text-[12px] text-slate-600 mt-1.5 leading-relaxed">
                  Click <span className="font-semibold text-[#0033A0]">Request deployment</span> on any model — IT packages it (BigFix / Intune) and pushes to your device. Approved models arrive silently in the background; you&apos;ll see them appear in your MyPC apps.
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-3 border-slate-200 shadow-sm">
            <div className="flex flex-wrap gap-1.5">
              {["all", "LLM", "LLM · Code", "Speech-to-Text", "Text-to-Image"].map((m) => (
                <button
                  key={m}
                  data-testid={`marketplace-modality-${m}`}
                  onClick={() => setModality(m)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                    modality === m
                      ? "bg-[#0033A0] text-white border-[#0033A0]"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {m === "all" ? "All modalities" : m}
                </button>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="models-grid">
            {models.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500 text-sm border border-dashed border-slate-300 rounded-md">
                No models match your filters.
              </div>
            )}
            {models.map((m) => <ModelCard key={m.id} model={m} onDeploy={requestDeploy} />)}
          </div>
        </>
      )}

      {tab === "resources" && (
        <>
          {/* Featured strip */}
          {featuredResources.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <Star size={12} className="text-amber-500" /> Featured this quarter
              </div>
              <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Curated by {brand.orgName} IT Academy</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3" data-testid="featured-resources">
                {featuredResources.map((r) => (
                  <ResourceCard key={r.id} resource={r} featured />
                ))}
              </div>
            </div>
          )}

          <Card className="p-3 border-slate-200 shadow-sm">
            <div className="flex flex-wrap gap-1.5">
              <button
                data-testid="resource-type-all"
                onClick={() => setResourceType("all")}
                className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${resourceType === "all" ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                All ({resources.length})
              </button>
              {resourceTypes.map((t) => (
                <button
                  key={t.id}
                  data-testid={`resource-type-${t.id}`}
                  onClick={() => setResourceType(t.id)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${resourceType === t.id ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resources-grid">
            {resources.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500 text-sm border border-dashed border-slate-300 rounded-md">
                No resources match your filters.
              </div>
            )}
            {resources.map((r) => <ResourceCard key={r.id} resource={r} />)}
          </div>
        </>
      )}

      {/* Legend */}
      <Card className="p-4 border-slate-200 shadow-sm bg-slate-50/60">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <BadgeCheck size={11} className="text-[#0033A0]" /> How execution mix is read
        </div>
        <div className="mt-2 flex items-center gap-4 text-[11px] flex-wrap">
          <div className="flex items-center gap-1.5"><span className="w-3 h-1.5 bg-slate-400 rounded-full" /> CPU · fallback compute</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-1.5 bg-[#0033A0] rounded-full" /> NPU · Copilot+ acceleration</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-1.5 bg-[#DA291C] rounded-full" /> GPU · discrete / creative workloads</div>
        </div>
      </Card>

      <AppDetailDialog
        app={openApp}
        open={!!openApp}
        onOpenChange={(v) => !v && setOpenApp(null)}
        onInstall={requestInstall}
      />
      <SubmitSolutionDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        onSubmitted={() => {}}
      />
    </div>
  );
}

function ResourceIcon({ type, size = 14, className = "" }) {
  const Ic = {
    video: Play, workshop: GraduationCap, doc: FileText, best_practice: Lightbulb,
    model_guide: BookOpen, blog: FileText,
  }[type] || FileText;
  return <Ic size={size} className={className} />;
}

function ResourceCard({ resource, featured = false }) {
  const timeLabel = resource.duration_min ? `${resource.duration_min} min` : resource.read_min ? `${resource.read_min} min read` : "";
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`resource-card-${resource.id}`}
      className={`group border border-slate-200 rounded-md bg-white hover:shadow-md hover:border-[#0033A0]/40 transition-all overflow-hidden flex flex-col ${featured ? "" : ""}`}
    >
      <div className="h-24 w-full grid place-items-center relative" style={{ backgroundColor: resource.thumbnail_bg || "#0033A0" }}>
        <ResourceIcon type={resource.type} size={30} className="text-white/90 group-hover:scale-110 transition-transform" />
        <Badge className="absolute top-2 right-2 bg-white/95 text-slate-700 hover:bg-white text-[9px]">{resource.type_label}</Badge>
        {resource.featured && (
          <Badge className="absolute top-2 left-2 bg-amber-500 hover:bg-amber-500 text-white text-[9px]">
            <Star size={9} className="mr-0.5 fill-current" /> Featured
          </Badge>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="font-display font-bold text-sm text-slate-900 leading-tight group-hover:text-[#0033A0] transition-colors">{resource.title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{resource.provider}</div>
        <p className="text-[11px] text-slate-600 mt-2 line-clamp-2 leading-relaxed">{resource.description}</p>

        <div className="mt-2 flex flex-wrap gap-1">
          {resource.topics?.slice(0, 3).map((t) => (
            <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t}</span>
          ))}
        </div>

        <div className="mt-auto pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
          <span className="flex items-center gap-1">
            <ResourceIcon type={resource.type} size={10} />
            {timeLabel}
          </span>
          <span>{resource.level}</span>
        </div>
      </div>
    </a>
  );
}
